import React, { useState } from "react";
import {
    useGiftSharesMutation,
    useGetSharesStatisticsQuery,
    useGetSharesSettingsQuery,
    useUpdateSharesSettingsMutation,
    useManualPayCreditSharesMutation,
} from "../store/api";
import { UserAutocomplete } from "../components/UserAutocomplete";

export const VouchersPage: React.FC = () => {
    const [sharesForm, setSharesForm] = useState({ user_id: "", num: "" });
    const [manualCreditForm, setManualCreditForm] = useState({
        user_id: "",
        amount: "",
    });
    const [sharesSettingsForm, setSharesSettingsForm] = useState({
        available_for_sale: "",
        shares_credit: "",
        shares_sell: "",
    });

    // Мутации
    const [giftSharesFn, { isLoading: isGifting }] = useGiftSharesMutation();
    const [manualPayCreditSharesFn, { isLoading: isPayingCreditShares }] =
        useManualPayCreditSharesMutation();

    // Управление акциями
    const {
        data: sharesStatistics,
        isLoading: isLoadingStatistics,
        refetch: refetchSharesStatistics,
    } = useGetSharesStatisticsQuery();
    const {
        data: sharesSettings,
        isLoading: isLoadingSettings,
        refetch: refetchSettings,
    } = useGetSharesSettingsQuery();
    const [updateSettings, { isLoading: isUpdatingSettings }] =
        useUpdateSharesSettingsMutation();

    // Загрузка текущих настроек акций в форму
    React.useEffect(() => {
        if (sharesSettings?.data) {
            setSharesSettingsForm({
                available_for_sale:
                    sharesSettings.data.available_for_sale.toString(),
                shares_credit: sharesSettings.data.shares_credit.toString(),
                shares_sell: sharesSettings.data.shares_sell.toString(),
            });
        }
    }, [sharesSettings]);

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Ваучеры
            </h1>

            {/* Статистика акций */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    Статистика акций
                </h3>
                {isLoadingStatistics ? (
                    <div className="text-sm text-gray-500">Загрузка...</div>
                ) : sharesStatistics?.data ? (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {/* Купленные акции */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Купленные
                                </h4>
                            </div>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {sharesStatistics.data.purchased.total}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                👥 {sharesStatistics.data.purchased.users}{" "}
                                польз.
                            </p>
                        </div>

                        {/* Подарочные акции */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Подарочные
                                </h4>
                            </div>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {sharesStatistics.data.gift.total}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                👥 {sharesStatistics.data.gift.users} польз.
                            </p>
                        </div>

                        {/* Акции от пользователей */}
                        <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    От польз.
                                </h4>
                            </div>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                {sharesStatistics.data.from_users.total}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                👥 {sharesStatistics.data.from_users.users}{" "}
                                польз.
                            </p>
                        </div>

                        {/* Кредитные акции */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Кредитные
                                </h4>
                            </div>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                {sharesStatistics.data.credit.total}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                👥 {sharesStatistics.data.credit.users} польз.
                            </p>
                        </div>

                        {/* Всего акций */}
                        <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Всего
                                </h4>
                            </div>
                            <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                                {sharesStatistics.data.total_shares}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                👥 {sharesStatistics.data.total_users} польз.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-gray-500">Нет данных</div>
                )}

                {/* Управление глобальными настройками акций */}
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white mt-4">
                    Управление доступными акциями
                </h3>
                {isLoadingSettings ? (
                    <div className="text-sm text-gray-500">Загрузка...</div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Доступно для продажи
                                </label>
                                <input
                                    type="number"
                                    value={
                                        sharesSettingsForm.available_for_sale
                                    }
                                    onChange={(e) =>
                                        setSharesSettingsForm({
                                            ...sharesSettingsForm,
                                            available_for_sale: e.target.value,
                                        })
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Акции в кредит
                                </label>
                                <input
                                    type="number"
                                    value={sharesSettingsForm.shares_credit}
                                    onChange={(e) =>
                                        setSharesSettingsForm({
                                            ...sharesSettingsForm,
                                            shares_credit: e.target.value,
                                        })
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Акции на продажу
                                </label>
                                <input
                                    type="number"
                                    value={sharesSettingsForm.shares_sell}
                                    onChange={(e) =>
                                        setSharesSettingsForm({
                                            ...sharesSettingsForm,
                                            shares_sell: e.target.value,
                                        })
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <button
                            onClick={async () => {
                                if (sharesSettingsForm.available_for_sale) {
                                    try {
                                        await updateSettings({
                                            available_for_sale: parseInt(
                                                sharesSettingsForm.available_for_sale
                                            ),
                                            shares_credit:
                                                sharesSettingsForm.shares_credit
                                                    ? parseInt(
                                                          sharesSettingsForm.shares_credit
                                                      )
                                                    : 0,
                                            shares_sell:
                                                sharesSettingsForm.shares_sell
                                                    ? parseInt(
                                                          sharesSettingsForm.shares_sell
                                                      )
                                                    : 0,
                                        }).unwrap();
                                        window.alert("Настройки акций обновлены");
                                        refetchSettings();
                                    } catch (error: any) {
                                        window.alert(
                                            "Ошибка: " +
                                                (error.data?.message ||
                                                    error.message)
                                        );
                                    }
                                }
                            }}
                            disabled={isUpdatingSettings}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md disabled:opacity-50"
                        >
                            {isUpdatingSettings
                                ? "Обновление..."
                                : "Обновить настройки"}
                        </button>
                    </div>
                )}
            </div>

            {/* Подарить акции */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    Подарить акции пользователю
                </h3>
                <div className="flex gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ID пользователя
                        </label>
                        <UserAutocomplete
                            value={sharesForm.user_id}
                            onChange={(value) =>
                                setSharesForm({
                                    ...sharesForm,
                                    user_id: value,
                                })
                            }
                            placeholder="Введите ID, email, имя..."
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Количество
                        </label>
                        <input
                            type="number"
                            value={sharesForm.num}
                            onChange={(e) =>
                                setSharesForm({
                                    ...sharesForm,
                                    num: e.target.value,
                                })
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0"
                        />
                    </div>
                    <button
                        onClick={async () => {
                            if (sharesForm.user_id && sharesForm.num) {
                                try {
                                    await giftSharesFn({
                                        user_id: parseInt(sharesForm.user_id),
                                        num: parseInt(sharesForm.num),
                                    }).unwrap();
                                    window.alert("Акции подарены успешно");
                                    setSharesForm({ user_id: "", num: "" });
                                    refetchSharesStatistics();
                                } catch (error: any) {
                                    window.alert(
                                        "Ошибка: " +
                                            (error.data?.message ||
                                                error.message)
                                    );
                                }
                            }
                        }}
                        disabled={isGifting}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md disabled:opacity-50"
                    >
                        {isGifting ? "Дарение..." : "Подарить"}
                    </button>
                </div>
            </div>

            {/* Ручная оплата кредитных акций */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    Ручная оплата кредитных акций
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    Сумма зачисляется на USDT-кошелек пользователя и сразу списывается в кредитные акции. В статистике будет запись credit_fee_manual.
                </p>
                <div className="flex gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ID пользователя
                        </label>
                        <UserAutocomplete
                            value={manualCreditForm.user_id}
                            onChange={(value) =>
                                setManualCreditForm({
                                    ...manualCreditForm,
                                    user_id: value,
                                })
                            }
                            placeholder="Введите ID, email, имя..."
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Сумма платежа
                        </label>
                        <input
                            type="number"
                            value={manualCreditForm.amount}
                            onChange={(e) =>
                                setManualCreditForm({
                                    ...manualCreditForm,
                                    amount: e.target.value,
                                })
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                        />
                    </div>
                    <button
                        onClick={async () => {
                            if (manualCreditForm.user_id && manualCreditForm.amount) {
                                try {
                                    await manualPayCreditSharesFn({
                                        user_id: parseInt(manualCreditForm.user_id),
                                        amount: parseFloat(manualCreditForm.amount),
                                    }).unwrap();
                                    window.alert("Оплата кредитных акций проведена");
                                    setManualCreditForm({ user_id: "", amount: "" });
                                    refetchSharesStatistics();
                                } catch (error: any) {
                                    window.alert(
                                        "Ошибка: " +
                                            (error.data?.message || error.message)
                                    );
                                }
                            }
                        }}
                        disabled={isPayingCreditShares}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-md disabled:opacity-50"
                    >
                        {isPayingCreditShares ? "Проведение..." : "Провести оплату"}
                    </button>
                </div>
            </div>
        </div>
    );
};

